import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftOnRectangleIcon,
  Bars3BottomLeftIcon,
  BookmarkSquareIcon,
  ChevronRightIcon,
  GiftIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import PreviewCard from "./PreviewCard";
import styles from "./Sidebar.module.css";

export interface ContentItem {
  id?: string;
  title: string;
  type: "thread" | "question";
  timestamp: string;
  answerPreview?: string;
  activeMessageId?: string | null;
  processingStatus?: "pending" | "processing" | "complete" | "error" | null;
  errorMessage?: string | null;
}

interface SidebarProps {
  onItemSelect?: (item: ContentItem) => void;
  refreshTrigger?: number;
  selectedItemId?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isMobileVisible?: boolean;
  screenSize?: "desktop" | "tablet" | "mobile";
  onMobileClose?: () => void;
  subscriptionPlan?: string | null;
  activeThreads?: Record<
    string,
    {
      messageId: string;
      status: "pending" | "processing" | "error";
      errorMessage?: string;
    }
  >;
}

const THREAD_PAGE_SIZE = 30;

const getThreadTimestamp = (thread: any) =>
  new Date(thread.last_message_at || thread.created_at || Date.now()).toISOString();

const formatThreadTitle = (title: string | null | undefined) => {
  const cleanTitle = (title || "New Thread").trim().replace(/\s+/g, " ");
  return cleanTitle || "New Thread";
};

const mapThreadToItem = (thread: any): ContentItem => ({
  id: thread.thread_id,
  title: formatThreadTitle(thread.title),
  type: "thread",
  timestamp: getThreadTimestamp(thread),
  answerPreview: thread.last_message_preview || null,
  activeMessageId: thread.active_message_id || null,
  processingStatus: thread.processing_status || null,
  errorMessage: thread.error_message || null,
});

const SidebarSkeleton = memo(() => (
  <div className={styles.skeletonList} aria-label="Loading chats">
    {Array.from({ length: 7 }).map((_, index) => (
      <div key={index} className={styles.skeletonCard}>
        <span className={styles.skeletonLines}>
          <span />
          <span />
        </span>
      </div>
    ))}
  </div>
));

const Sidebar: React.FC<SidebarProps> = ({
  onItemSelect,
  refreshTrigger,
  selectedItemId,
  isCollapsed,
  onToggleCollapse,
  isMobileVisible = false,
  screenSize = "desktop",
  onMobileClose,
  subscriptionPlan = null,
  activeThreads = {},
}) => {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [threadsOffset, setThreadsOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const { user, session, signOut } = useAuthContext();
  const navigate = useNavigate();

  const fetchThreads = useCallback(
    async (offset = 0): Promise<ContentItem[]> => {
      if (!user || !session) return [];

      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/api/threads/${user.id}?limit=${THREAD_PAGE_SIZE}&offset=${offset}`,
        { headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) {
        throw new Error(`Error fetching threads: ${response.statusText}`);
      }

      const threadsData = await response.json();
      setHasMoreThreads(threadsData.length === THREAD_PAGE_SIZE);
      return threadsData.map(mapThreadToItem);
    },
    [session, user]
  );

  const refreshThreads = useCallback(async () => {
    if (!user || !session) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const threads = await fetchThreads(0);
      setItems(threads);
      setThreadsOffset(THREAD_PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching threads:", err);
      setError("Failed to load chats. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchThreads, session, user]);

  const loadMoreThreads = useCallback(async () => {
    if (!user || !hasMoreThreads || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const moreThreads = await fetchThreads(threadsOffset);
      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        return [...prev, ...moreThreads.filter((item) => !seen.has(item.id))];
      });
      setThreadsOffset((prev) => prev + THREAD_PAGE_SIZE);
    } catch (err) {
      console.error("Error loading more threads:", err);
      setError("Failed to load more chats. Please try again.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchThreads, hasMoreThreads, isLoadingMore, threadsOffset, user]);

  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    if (isFetchingRef.current) return;

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    const delay = timeSinceLastFetch < 700 ? 700 - timeSinceLastFetch : 160;

    fetchTimeoutRef.current = setTimeout(async () => {
      isFetchingRef.current = true;
      lastFetchTimeRef.current = Date.now();
      await refreshThreads();
      isFetchingRef.current = false;
    }, delay);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [refreshThreads, refreshTrigger]);

  useEffect(() => {
    if (!selectedItemId) {
      setSelectedItem(null);
      return;
    }
    const foundItem = items.find((item) => item.id === selectedItemId);
    if (foundItem) setSelectedItem(foundItem);
  }, [items, selectedItemId]);

  const visibleItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const withActiveState = items.map((item) => {
      if (!item.id) return item;
      const activeThread = activeThreads[item.id];
      if (!activeThread) return item;
      return {
        ...item,
        activeMessageId: activeThread.messageId,
        processingStatus: activeThread.status,
        errorMessage: activeThread.errorMessage || item.errorMessage,
      };
    });

    if (!query) return withActiveState;
    return withActiveState.filter((item) =>
      `${item.title} ${item.answerPreview || ""}`.toLowerCase().includes(query)
    );
  }, [activeThreads, items, searchQuery]);

  const handleSelectItem = (item: ContentItem) => {
    setSelectedItem(item);
    onItemSelect?.(item);
  };

  const handleNewChat = () => {
    setSearchQuery("");
    navigate("/chat");
    if (screenSize === "mobile") onMobileClose?.();
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (
      target.scrollHeight - target.scrollTop - target.clientHeight < 120 &&
      hasMoreThreads &&
      !isLoadingMore &&
      !searchQuery.trim()
    ) {
      loadMoreThreads();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (err) {
      console.error("Error logging out:", err);
    }
  };

  const sidebarClasses = [
    styles.sidebarContainer,
    screenSize === "mobile" ? styles.mobileSidebar : styles.desktopSidebar,
    screenSize === "mobile" && isMobileVisible ? styles.mobileVisible : "",
    isCollapsed && screenSize !== "mobile" ? styles.sidebarCollapsed : "",
    !user || !session ? styles.noSession : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className={sidebarClasses} aria-label="Chat history">
      <div className={styles.sidebarHeader}>
        <button
          type="button"
          onClick={screenSize === "mobile" ? onMobileClose : onToggleCollapse}
          className={styles.iconButton}
          aria-label={screenSize === "mobile" ? "Close sidebar" : "Hide sidebar"}
        >
          {screenSize === "mobile" ? (
            <XMarkIcon className={styles.headerIcon} />
          ) : (
            <Bars3BottomLeftIcon className={styles.headerIcon} />
          )}
        </button>

        <div className={styles.headerTitleBlock}>
          <h2 className={styles.headerTitle}>Chats</h2>
        </div>

        <button
          type="button"
          onClick={handleNewChat}
          className={`${styles.iconButton} ${styles.newChatButton}`}
          aria-label="Start new chat"
        >
          <PencilSquareIcon className={styles.headerIcon} />
        </button>
      </div>

      {user && (
        <nav className={styles.workspaceNav} aria-label="Personal workspace">
          <Link to="/notes" className={styles.notesLink} onClick={onMobileClose}>
            <BookmarkSquareIcon className={styles.notesIcon} aria-hidden="true" />
            <span>Notes</span>
            <ChevronRightIcon className={styles.notesChevron} aria-hidden="true" />
          </Link>
        </nav>
      )}
      <div className={styles.searchWrap}>
        <MagnifyingGlassIcon className={styles.searchIcon} />
        <input
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className={styles.searchInput}
          placeholder="Search chats"
          aria-label="Search chats"
        />
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.previewCardsContainer} onScroll={handleScroll}>
        {isLoading ? (
          <SidebarSkeleton />
        ) : visibleItems.length === 0 ? (
          <div className={styles.previewCardsEmpty}>
            {searchQuery.trim()
              ? "No chats match your search."
              : "No chats yet. Start a new conversation."}
          </div>
        ) : (
          visibleItems.map((item) => (
            <PreviewCard
              key={item.id || item.title}
              title={item.title}
              timestamp={item.timestamp}
              onSelect={() => handleSelectItem(item)}
              isSelected={selectedItem?.id === item.id}
              preview={item.answerPreview}
              processingStatus={item.processingStatus}
            />
          ))
        )}

        {isLoadingMore && (
          <div className={styles.loadingMoreIndicator}>Loading more chats...</div>
        )}
      </div>

      <div className={styles.bottomMenu}>
        <button
          type="button"
          onClick={() => navigate("/dashboard?tab=referrals")}
          className={styles.shareEarnButton}
        >
          <GiftIcon className={styles.menuIcon} />
          <span>Share & Earn Credits</span>
        </button>

        {!subscriptionPlan && (
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            className={styles.upgradeButton}
          >
            <SparklesIcon className={styles.menuIcon} />
            <span>Get unlimited credits</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => window.open("https://wa.me/966558096476", "_blank")}
          className={styles.menuButton}
        >
          <QuestionMarkCircleIcon className={styles.menuIcon} />
          <span>Support</span>
        </button>

        <button type="button" onClick={handleLogout} className={styles.logoutButton}>
          <ArrowLeftOnRectangleIcon className={styles.menuIcon} />
          <span>Log out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
